import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Header from '../../components/header/Header';
import Message from '../../components/message/Message';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import { Dealer } from '../../gql/graphql';
import { SegmentProps } from '../../state-machine/actions/segment';
import { ExitCodes, modalLoaderHelper, ModalMessage, ModalMessageFailure, modalResolver } from '../../helpers/modal/modal.helper';
import { inIframe } from '../../helpers';
import { currentDomain } from '../../helpers/window/window.helpers';
import { SUPPORT_PHONE_NUMBER, SUPPORT_PHONE_TEL } from '../../constants';

const ExpiredLinkWrapper = styled.div`
  font-family: ${fontFamilies.gilroyRegular};

  a {
    text-decoration: underline;
    color: ${colors.koalafiBlue};
  }
`;

// This will need to be dynamic as soon as we start to route other kinds of expiration to this component.
type LinkType = 'OOBA';

type Props = SegmentProps & {
  orderId: string;
  dealer: Dealer | undefined;
  linkType?: LinkType;
};

const ExpiredLink: React.FC<Props> = ({ orderId, dealer, linkType = 'OOBA', track, page, identify }) => {
  const { t } = useTranslation();

  const domains = dealer?.domains ?? [currentDomain()];

  useEffect(() => {
    page('Expired Link');
    track('Expired Link Viewed', { orderId, linkType });
    modalLoaderHelper({ stopSpinner: true }, domains);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExit = () => {
    track('Expired Link Exited', { orderId, linkType });
    const payload: ModalMessage = {
      wcfSuccess: false,
      closeModal: true,
      payload: {
        reason: 'closed expired link modal',
        reasonCode: ExitCodes.generalCloseModal,
        orderId,
      } as ModalMessageFailure,
    };
    modalResolver(payload, domains);
  };

  const body = (
    <Trans
      i18nKey={($) => $.pageExpiredLink.body}
      values={{ supportPhone: SUPPORT_PHONE_NUMBER }}
      components={{
        supportLink: <a href={SUPPORT_PHONE_TEL} aria-label={t(($) => $.pageExpiredLink.contactNumber)} />,
      }}
    />
  );

  return (
    <ExpiredLinkWrapper id="ExpiredLink">
      <Header
        orderId={orderId}
        dealer={dealer}
        goBackFunc={undefined}
        currentNode={['ExpiredLink']}
        track={track}
        page={page}
        identify={identify}
        highestSupportedApplicationMilestone={3}
      />
      <Message
        heading={t(($) => $.pageExpiredLink.heading)}
        body={body}
        buttonText={t(($) => $.buttonLabels.exit)}
        buttonClick={handleExit}
        shouldShowButton={inIframe()}
      />
    </ExpiredLinkWrapper>
  );
};

export default ExpiredLink;
